import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { NAV_LINKS, CONTACT } from "@/data/content";

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate(`/#${id}`);
    } else {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  const onLight = scrolled || location.pathname !== "/";

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        onLight ? "bg-white/90 backdrop-blur-xl border-b border-stone-200/60 py-2.5" : "bg-transparent py-4"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
        <button
          data-testid="logo-home-button"
          onClick={() => go("top")}
          className="flex items-center gap-3 group"
        >
          <img src="/assets/logo.png" alt="Logo A.S.I. Sicilia ETS" className="h-12 w-auto object-contain" />
          <span className="hidden sm:block text-left leading-tight">
            <span className={`block font-heading text-lg font-semibold ${onLight ? "text-stone-900" : "text-white"}`}>
              A.S.I. Sicilia ETS
            </span>
            <span className={`block text-[10px] tracking-[0.18em] uppercase ${onLight ? "text-stone-500" : "text-stone-200"}`}>
              Affrontiamo la Sordità Insieme
            </span>
          </span>
        </button>

        <nav className="hidden lg:flex items-center gap-7">
          {NAV_LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-${l.id}`}
              onClick={() => go(l.id)}
              className={`text-sm font-medium transition-colors ${onLight ? "text-stone-700 hover:text-lime-900" : "text-stone-100 hover:text-white"}`}
            >
              {l.label}
            </button>
          ))}
          <a
            href={CONTACT.areaRiservata}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-area-riservata"
            className={`text-sm font-medium transition-colors ${onLight ? "text-stone-700 hover:text-lime-900" : "text-stone-100 hover:text-white"}`}
          >
            Area riservata
          </a>
          <button
            data-testid="nav-cta-aiuto"
            onClick={() => go("aiuto")}
            className="rounded-full bg-[#E2725B] text-white px-5 py-2.5 text-sm font-semibold hover:bg-[#d5604a] transition-colors"
          >
            Chiedi aiuto
          </button>
        </nav>

        <button
          data-testid="mobile-menu-toggle"
          className={`lg:hidden ${onLight ? "text-stone-900" : "text-white"}`}
          onClick={() => setOpen((o) => !o)}
          aria-label="Apri menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden bg-white border-t border-stone-200 mt-2.5">
          <div className="px-6 py-4 flex flex-col gap-4">
            {NAV_LINKS.map((l) => (
              <button
                key={l.id}
                data-testid={`mobile-nav-${l.id}`}
                onClick={() => go(l.id)}
                className="text-left text-base font-medium text-stone-800"
              >
                {l.label}
              </button>
            ))}
            <a
              href={CONTACT.areaRiservata}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="mobile-nav-area-riservata"
              className="text-left text-base font-medium text-stone-800"
            >
              Area riservata
            </a>
            <button
              data-testid="mobile-nav-cta"
              onClick={() => go("aiuto")}
              className="rounded-full bg-[#E2725B] text-white px-5 py-3 text-sm font-semibold"
            >
              Chiedi aiuto
            </button>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
